import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import moment from "moment";
import logo from "../icons/latest-logo.png";
import "./leaveApplicationPrint.css";

const fmtDate = (value) => {
  if (!value) return "—";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : String(value);
};

const display = (value, fallback = "—") => {
  if (value === 0 || value === "0") return String(value);
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const absenceLabel = (application) => {
  const status = application?.leave_status;
  if (status === "Leave" || status === "Absent") return status;
  return application?.leave_type ? "Leave" : "Absent";
};

const field = (label, value, extraClass = "") => `
  <div class="laf-field ${extraClass}">
    <span class="laf-label">${escapeHtml(label)}</span>
    <span class="laf-value">${escapeHtml(value)}</span>
  </div>
`;

const row = (label, value) => `
  <tr>
    <th>${escapeHtml(label)}</th>
    <td>${escapeHtml(value)}</td>
  </tr>
`;

/**
 * Download an approved leave application as a one-page A4 form.
 * Layout matches the client assessment form: logo, red title, section bars.
 */
export async function downloadLeaveApplicationPdf(application, employee = {}) {
  if (!application) return;

  const status = String(application.application_status || "").trim();
  if (status.toLowerCase() !== "approved") {
    throw new Error("Only approved leave applications can be downloaded.");
  }

  const userInfo = application.user_info || application.userInfo || {};
  const branchInfo = application.branch_info || application.branchInfo || {};
  const employeeName =
    employee.name ||
    userInfo.name ||
    `${employee.first_name || ""} ${employee.last_name || ""}`.trim() ||
    "Employee";
  const employeeId = display(
    employee.uid || employee.id || userInfo.uid || userInfo.id || application.user_id
  );
  const branchName = display(
    employee.branch ||
      employee.branch_name ||
      branchInfo.name ||
      application.branch_name
  );
  const designation = display(
    employee.designation ||
      employee.designation_name ||
      application.designation
  );
  const leaveType = application.leave_type
    ? `${application.leave_type} Leave`
    : "Leave";
  const category =
    application.category === "Full"
      ? "Full Day"
      : application.category === "Half"
        ? "Half Day"
        : display(application.category, "Full Day");
  const refNo = `LA-${moment(
    application.submission_date || application.from || undefined
  ).format("YYYY")}-${String(application.id).padStart(4, "0")}`;
  const appliedOn = fmtDate(application.submission_date || application.created_at);
  const approvedOn = fmtDate(application.review_date);

  const host = document.createElement("div");
  host.className = "laf-host";
  host.innerHTML = `
    <div class="laf-page">
      <div class="laf-head">
        <img class="laf-logo" src="${logo}" alt="Vostro World" />
        <div class="laf-head-text">
          <h2 class="laf-title">Leave Application Form</h2>
          <p class="laf-subtitle">Approved Copy</p>
        </div>
      </div>

      <div class="laf-meta">
        ${field("Reference", refNo, "laf-field-accent")}
        ${field("Print Date", moment().format("DD MMM YYYY"))}
        <div class="laf-field">
          <span class="laf-label">Status</span>
          <span class="laf-value laf-value-strong">APPROVED</span>
        </div>
      </div>

      <div class="laf-section">Employee</div>
      <div class="laf-grid-2">
        ${field("Name", employeeName, "laf-field-accent")}
        ${field("Employee ID", employeeId)}
        ${field("Branch", branchName)}
        ${field("Designation", designation)}
      </div>

      <div class="laf-section">Leave</div>
      <table class="laf-table">
        <tbody>
          ${row("Leave Type", leaveType)}
          ${row("Duration", category)}
          ${row("Absence", absenceLabel(application))}
          ${row("From", fmtDate(application.from))}
          ${row("To", fmtDate(application.to))}
          ${row("Total Days", display(application.number_of_leaves))}
        </tbody>
      </table>

      <div class="laf-section">Reason</div>
      <div class="laf-reason">${escapeHtml(display(application.reason, "No reason recorded."))}</div>

      <div class="laf-section">Timeline</div>
      <div class="laf-grid-2">
        ${field("Applied On", appliedOn, "laf-field-accent")}
        ${field("Approved On", approvedOn)}
      </div>

      <div class="laf-signs">
        <div>
          <div class="laf-sign-line">
            <div class="laf-sign-name">${escapeHtml(employeeName)}</div>
            <div class="laf-sign-role">Employee</div>
          </div>
        </div>
        <div>
          <div class="laf-sign-line">
            <div class="laf-sign-name">Human Resources</div>
            <div class="laf-sign-role">Authorized signature · ${escapeHtml(approvedOn)}</div>
          </div>
        </div>
      </div>

      <div class="laf-note">This is a system-generated approved leave copy for the official record.</div>
      <div class="laf-footer">
        <span>Vostro World · Leave Application</span>
        <span>Page 1 of 1</span>
      </div>
    </div>
  `;

  document.body.appendChild(host);

  try {
    const image = host.querySelector("img");
    if (image && !image.complete) {
      await new Promise((resolve) => {
        image.onload = resolve;
        image.onerror = resolve;
      });
    }

    const page = host.querySelector(".laf-page");
    const canvas = await html2canvas(page, {
      scale: 2,
      backgroundColor: "#ffffff",
      logging: false,
      useCORS: true,
      allowTaint: true,
      width: page.scrollWidth,
      height: page.scrollHeight,
      windowWidth: page.scrollWidth,
      windowHeight: page.scrollHeight,
    });

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true,
    });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgData = canvas.toDataURL("image/jpeg", 0.92);
    const imgHeight = (canvas.height * pageWidth) / canvas.width;

    if (imgHeight <= pageHeight) {
      pdf.addImage(imgData, "JPEG", 0, 0, pageWidth, imgHeight);
    } else {
      const fitWidth = (canvas.width * pageHeight) / canvas.height;
      pdf.addImage(imgData, "JPEG", (pageWidth - fitWidth) / 2, 0, fitWidth, pageHeight);
    }

    const safeName = employeeName.replace(/[^\w-]+/g, "_").slice(0, 35) || "employee";
    pdf.save(`Leave_Application_${safeName}_${refNo}.pdf`);
  } finally {
    host.remove();
  }
}
